import { useEffect, useMemo, useState } from 'preact/hooks';
import qrcode from 'qrcode-generator';
import { price as fmtPrice } from '../format.js';
import { API, type TokenSummary } from '../api.js';

/** Token picture, or a generated disc when there is none or it fails to load. */
export function Avatar({ token, size = 48 }: { token: Pick<TokenSummary, 'id' | 'symbol' | 'image'>; size?: number }) {
  const [broken, setBroken] = useState(false);
  const hue = useMemo(() => [...token.id].reduce((h, c) => (h * 31 + c.charCodeAt(0)) % 360, 7), [token.id]);
  // Pictures come through our server (/img), never straight from the creator's host, which would see every viewer.
  if (token.image && !broken)
    return (
      <img
        class="avatar"
        src={`${API}/img/${encodeURIComponent(token.id)}`}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        referrerpolicy="no-referrer"
        style={{ width: size, height: size }}
        onError={() => setBroken(true)}
      />
    );
  return (
    <div class="avatar" aria-hidden="true" style={{ width: size, height: size, background: `hsl(${hue} 55% 62%)`, fontSize: size * 0.45 }}>
      {token.symbol.slice(0, 1)}
    </div>
  );
}

/** Price per token in ZEC, with long runs of zeros collapsed: 0.0₈231 */
export function Price({ zatPerToken }: { zatPerToken: number }) {
  const p = fmtPrice(zatPerToken);
  if (!p.zeros) return <span class="price">{p.lead}</span>;
  return (
    <span class="price" title={`${(zatPerToken / 1e8).toExponential(3)} ZEC`}>
      {p.lead}
      <sub>{p.zeros}</sub>
      {p.digits}
    </span>
  );
}

export function Meter({ bps, graduated, label }: { bps: number; graduated?: boolean; label?: string }) {
  const pct = Math.min(100, bps / 100);
  return (
    <div
      class={`meter${graduated ? ' done' : ''}`}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
      aria-label={label ?? 'Progress to graduation'}
    >
      <i style={{ width: `${pct}%` }} />
    </div>
  );
}

export function QR({ text }: { text: string }) {
  const svg = useMemo(() => {
    const q = qrcode(0, 'M');
    q.addData(text);
    q.make();
    return q.createSvgTag({ cellSize: 4, margin: 0, scalable: true });
  }, [text]);
  return <div class="qr" dangerouslySetInnerHTML={{ __html: svg }} />;
}

/** Re-render every `ms` so relative times stay fresh. */
export function useTicker(ms = 10_000) {
  const [, set] = useState(0);
  useEffect(() => {
    const t = setInterval(() => set((x) => x + 1), ms);
    return () => clearInterval(t);
  }, [ms]);
}

let toastTimer: ReturnType<typeof setTimeout> | undefined;
type ToastState = { text: string; bad?: boolean } | null;
const toastSubs = new Set<(t: ToastState) => void>();
export function toast(text: string, bad = false) {
  toastSubs.forEach((f) => f({ text, bad }));
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toastSubs.forEach((f) => f(null)), 4500);
}
export function Toaster() {
  const [t, setT] = useState<ToastState>(null);
  useEffect(() => {
    toastSubs.add(setT);
    return () => void toastSubs.delete(setT);
  }, []);
  if (!t) return null;
  return (
    <div class={`toast${t.bad ? ' bad' : ''}`} role="status" aria-live="polite">
      {t.text}
    </div>
  );
}

export async function copy(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast('Copied');
  } catch {
    toast('Copy failed: select the text and copy it by hand', true);
  }
}
